import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ListTodo, Plus, X, CheckCircle2, Circle, Clock, AlertTriangle,
  ChevronRight, Sun, Loader2, Check, User, Zap, MoreVertical, Edit3, Trash2
} from 'lucide-react';
import { TaskModal, CompleteModal } from './Tasks/TaskModals';

const API = process.env.REACT_APP_BACKEND_URL;
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token') || localStorage.getItem('token')}` } });

const PRIORITY_COLORS = {
  urgent: 'border-l-red-500', high: 'border-l-orange-400', medium: 'border-l-blue-400', low: 'border-l-gray-300',
};

export default function TaskFAB({ user }) {
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState({});
  const [todayTasks, setTodayTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [showCompleteModal, setShowCompleteModal] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const panelRef = useRef(null);

  // Broadcast open/close so other floating widgets (e.g. AI Assistant FAB) can
  // re-position themselves to sit side-by-side rather than overlap.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('fab-state', { detail: { fab: 'tasks', open } }));
  }, [open]);

  const fetchData = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [sumRes, tasksRes] = await Promise.all([
        axios.get(`${API}/api/tasks/my-summary`, getAuth()),
        axios.get(`${API}/api/tasks?view=my_day&limit=20&sort_by=due_date&sort_order=1`, getAuth()),
      ]);
      setSummary(sumRes.data);
      setTodayTasks(tasksRes.data.tasks || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/';
        return;
      }
    }
    setLoading(false);
  };

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API}/api/tasks/users`, getAuth());
      setUsers(data.users || []);
    } catch {}
  };

  useEffect(() => {
    if (open) {
      fetchData();
      fetchUsers();
    }
  }, [open]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) && !e.target.closest('[data-testid="task-rail-tab"]') && !e.target.closest('[data-testid="task-create-modal"]')) {
        setOpen(false);
      }
    };
    if (open && !showCreateModal && !showCompleteModal) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, showCreateModal, showCompleteModal]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape' && !showCreateModal && !showCompleteModal) setOpen(false); };
    if (open) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, showCreateModal, showCompleteModal]);

  const handleToggle = async (taskId, currentStatus) => {
    // Always open CompleteModal for any task click
    const task = todayTasks.find(t => t.id === taskId);
    if (task) {
      setShowCompleteModal(task);
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await axios.delete(`${API}/api/tasks/${taskId}`, getAuth());
      toast.success('Task deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const refreshAll = () => { fetchData(); };

  const pendingCount = (summary.pending || 0) + (summary.overdue || 0);

  return (
    <>
      {/* Vertical Rail Tab — fixed on right edge, vertically centered */}
      <button
        onClick={() => setOpen(!open)}
        data-testid="task-rail-tab"
        className={`fixed right-0 top-1/2 -translate-y-1/2 z-40 flex items-center justify-center transition-all duration-300 ${
          open ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
        style={{ writingMode: 'vertical-rl' }}
      >
        <div className="text-white px-1.5 py-4 rounded-l-xl shadow-[0_8px_24px_rgba(217,119,6,0.40)] flex flex-col items-center gap-2 transition-all duration-180 ease-out hover:px-2 hover:shadow-[0_12px_32px_rgba(217,119,6,0.50)] group cursor-pointer" style={{ background: 'linear-gradient(135deg, #D97706 0%, #EA580C 60%, #DC2626 100%)' }}>
          <ListTodo className="w-4 h-4 rotate-0" style={{ writingMode: 'horizontal-tb' }} />
          <span className="text-[10px] font-semibold tracking-wider uppercase">Tasks</span>
          {pendingCount > 0 && (
            <span className="bg-red-500 text-white text-[9px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center" style={{ writingMode: 'horizontal-tb' }}>
              {pendingCount > 9 ? '9+' : pendingCount}
            </span>
          )}
        </div>
      </button>

      {/* Backdrop */}
      {open && !showCreateModal && !showCompleteModal && (
        <div
          className="fixed inset-0 bg-black/20 z-40 transition-opacity duration-300 md:bg-transparent"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Side Panel — slides from right */}
      <div
        ref={panelRef}
        className={`fixed top-0 right-0 z-50 h-full w-80 sm:w-[340px] bg-white/95 backdrop-blur-xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.35)] border-l border-amber-100/60 flex flex-col transition-transform duration-300 ease-in-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
        data-testid="task-side-panel"
      >
        {/* Panel Header with Add Task button */}
        <div className="text-white px-4 py-3 flex items-center justify-between shrink-0" style={{ background: 'linear-gradient(135deg, #D97706 0%, #EA580C 60%, #DC2626 100%)' }}>
          <div className="flex items-center gap-2">
            <Sun className="w-4 h-4 text-amber-400" />
            <span className="font-semibold text-sm">My Day</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setEditingTask(null); setShowCreateModal(true); }}
              className="flex items-center gap-1 px-2.5 py-1 bg-white/15 hover:bg-white/25 backdrop-blur-sm rounded-lg text-[11px] font-medium transition-colors"
              data-testid="rail-add-task-btn"
            >
              <Plus className="w-3 h-3" /> Add Task
            </button>
            <a href="/tasks" className="text-[11px] text-white/80 hover:text-white flex items-center gap-1 transition-colors" data-testid="view-all-tasks-link">
              View All <ChevronRight className="w-3 h-3" />
            </a>
            <button onClick={() => setOpen(false)} className="p-1 hover:bg-white/10 rounded-lg" data-testid="close-side-panel">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Summary Row */}
        <div className="grid grid-cols-4 gap-1 px-3 py-2.5 bg-gray-50 border-b text-center shrink-0">
          {[
            { l: 'Today', v: summary.due_today || 0, c: 'text-amber-600' },
            { l: 'Pending', v: summary.pending || 0, c: 'text-blue-600' },
            { l: 'Overdue', v: summary.overdue || 0, c: 'text-red-600' },
            { l: 'Done', v: summary.completed_today || 0, c: 'text-green-600' },
          ].map(s => (
            <div key={s.l} className="py-1">
              <div className={`text-lg font-bold ${s.c}`}>{s.v}</div>
              <div className="text-[10px] text-gray-500">{s.l}</div>
            </div>
          ))}
        </div>

        {/* Task List — scrollable */}
        <div className="flex-1 overflow-y-auto" onClick={() => setActiveMenu(null)}>
          {loading ? (
            <div className="text-center py-12"><Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" /></div>
          ) : todayTasks.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-xs">
              <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-green-300" />
              <p>All clear for today!</p>
              <p className="text-[10px] mt-1 text-gray-300">No pending tasks</p>
            </div>
          ) : (
            todayTasks.map(task => {
              const isOverdue = task.due_date && task.due_date < new Date().toISOString().split('T')[0] && !['completed', 'not_completed'].includes(task.status);
              const isAuto = (task.tags || []).includes('auto-created');
              return (
                <div key={task.id} onClick={() => setShowCompleteModal(task)} className={`flex items-center gap-2.5 px-3.5 py-2.5 border-b border-l-4 ${PRIORITY_COLORS[task.priority] || 'border-l-gray-200'} hover:bg-gray-50 transition-colors group cursor-pointer`} data-testid={`rail-task-${task.id}`}>
                  <div className="shrink-0" data-testid={`rail-toggle-${task.id}`}>
                    {task.status === 'completed' ? (
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                    ) : isOverdue ? (
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                    ) : (
                      <Circle className="w-4 h-4 text-gray-300 hover:text-blue-400 transition-colors" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className={`text-xs font-medium truncate ${task.status === 'completed' ? 'line-through text-gray-400' : 'text-gray-700'}`}>{task.title}</p>
                      {isAuto && <Zap className="w-3 h-3 text-teal-500 shrink-0" />}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {task.assigned_to?.name && task.assigned_to.id !== user?.id && (
                        <span className="text-[10px] text-gray-400 flex items-center gap-0.5"><User className="w-2.5 h-2.5" />{task.assigned_to.name}</span>
                      )}
                      {task.due_time && <span className="text-[10px] text-gray-400">{task.due_time}</span>}
                      {isOverdue && <span className="text-[9px] text-red-500 font-medium">Overdue</span>}
                    </div>
                  </div>
                  {/* 3-dot menu */}
                  <div className="relative shrink-0">
                    <button
                      onClick={(e) => { e.stopPropagation(); setActiveMenu(activeMenu === task.id ? null : task.id); }}
                      className="p-1 rounded-lg hover:bg-gray-200 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`rail-menu-${task.id}`}
                    >
                      <MoreVertical className="w-3.5 h-3.5 text-gray-400" />
                    </button>
                    {activeMenu === task.id && (
                      <div className="absolute right-0 top-7 z-50 bg-white border rounded-xl shadow-lg py-1 w-36" onMouseLeave={() => setActiveMenu(null)}>
                        <button
                          onClick={() => { setEditingTask(task); setShowCreateModal(true); setActiveMenu(null); }}
                          className="w-full px-3 py-1.5 text-left text-xs hover:bg-gray-50 flex items-center gap-2"
                          data-testid={`rail-edit-${task.id}`}
                        ><Edit3 className="w-3 h-3" /> Edit</button>
                        <button
                          onClick={() => { setShowCompleteModal(task); setActiveMenu(null); }}
                          className="w-full px-3 py-1.5 text-left text-xs hover:bg-gray-50 flex items-center gap-2"
                        ><Check className="w-3 h-3" /> Complete</button>
                        <button
                          onClick={() => { handleDelete(task.id); setActiveMenu(null); }}
                          className="w-full px-3 py-1.5 text-left text-xs hover:bg-red-50 text-red-600 flex items-center gap-2"
                          data-testid={`rail-delete-${task.id}`}
                        ><Trash2 className="w-3 h-3" /> Delete</button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Full Task Modal — opens over the side panel */}
      {showCreateModal && (
        <div data-testid="task-create-modal">
          <TaskModal
            editingTask={editingTask}
            setEditingTask={setEditingTask}
            setShowCreateModal={setShowCreateModal}
            templates={[]}
            users={users}
            user={user}
            refreshAll={refreshAll}
          />
        </div>
      )}

      {/* Complete Modal */}
      {showCompleteModal && (
        <CompleteModal
          showCompleteModal={showCompleteModal}
          setShowCompleteModal={setShowCompleteModal}
          setEditingTask={setEditingTask}
          setShowCreateModal={setShowCreateModal}
          refreshAll={refreshAll}
        />
      )}
    </>
  );
}
